import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { AppBar } from '@/components/AppBar';
import { PageContainer, PageInner } from '@/components/ui/PageContainer';
import { InlineAlert } from '@/components/ui/InlineAlert';
import { sendReset } from '@/lib/auth';
import { formatUserError } from '@/lib/errors';
import { t } from '@/i18n/vi';

export const Route = createFileRoute('/reset')({
  component: ResetScreen,
});

function ResetScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await sendReset(email.trim());
      setDone(true);
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        // Don't leak whether the email exists.
        setDone(true);
      } else {
        setErr(formatUserError(e).detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer>
      <AppBar title={t.authForgot} onBack={() => navigate({ to: '/login' })} right={<span />} />
      <PageInner className="pt-6">
        {done ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-border bg-surface px-4 py-3 text-[14px] text-ink-1">
              {t.authResetSent}
            </div>
            <Link
              to="/login"
              className="self-start text-[13px] font-medium text-[var(--brand)] hover:underline"
            >
              ← {t.authSignIn}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-ink-2">{t.authEmail}</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border border-border bg-surface px-3 py-2.5 text-[14px] text-ink-1"
              />
            </label>

            {err && <InlineAlert>{err}</InlineAlert>}

            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-[var(--brand)] py-2.5 text-[14px] font-semibold text-white disabled:opacity-60"
            >
              {busy ? t.authLoading : t.authSendReset}
            </button>

            <div className="mt-2 text-center text-[13px] text-ink-2">
              <Link to="/login" className="text-[var(--brand)] hover:underline">
                {t.authSignIn}
              </Link>
            </div>
          </form>
        )}
      </PageInner>
    </PageContainer>
  );
}
